import React, { useEffect, useState } from "react";
import { pilotApi } from "../../services/phase1Api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Rocket, ExternalLink, Loader2, Users, Settings2, CheckCircle2 } from "lucide-react";

const PilotParticipants = ({ session }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null);

  const load = () => {
    setLoading(true);
    pilotApi.participants(session).then(setData).catch(() => toast.error("Failed to load participants")).finally(() => setLoading(false));
  };
  useEffect(load, [session]);

  const convert = async (kind, id, label) => {
    if (!window.confirm(`Convert "${label}" from pilot to a standard paid account? This starts the normal payment workflow and keeps all existing work.`)) return;
    setConverting(id);
    try {
      await pilotApi.convert(kind, id, session);
      toast.success("Converted to paid — commercial workflow started");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally { setConverting(null); }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-foreground/50 py-10" data-testid="pilot-participants-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading participants…</div>;
  if (!data) return null;
  const t = data.totals || {};

  return (
    <div className="space-y-6" data-testid="pilot-participants">
      <div className="grid grid-cols-3 gap-3">
        {[["Pilot schools", t.pilot_schools], ["Pilot portfolios", t.pilot_portfolios], ["Diagnostics completed", t.diagnostics_completed]].map(([l, v]) => (
          <div key={l} className="rounded-xl border border-border bg-muted/30 p-4">
            <div className="text-2xl font-bold text-foreground" data-testid={`pilot-stat-${l.replace(/\s+/g, "-").toLowerCase()}`}>{v ?? 0}</div>
            <div className="text-[11px] uppercase tracking-wider text-foreground/40 mt-1">{l}</div>
          </div>
        ))}
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 mb-2">Pilot schools</p>
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid="pilot-schools-table">
            <thead className="bg-muted/50 text-foreground/60 text-xs">
              <tr><th className="text-left px-3 py-2">School</th><th className="text-left px-3 py-2">Contact</th><th className="text-left px-3 py-2">Diagnostics</th><th className="text-left px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {(data.schools || []).length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-foreground/40">No pilot schools yet.</td></tr>}
              {(data.schools || []).map((s) => (
                <tr key={s.tenant_id} className="border-t border-border" data-testid={`pilot-school-row-${s.tenant_id}`}>
                  <td className="px-3 py-2 font-medium text-foreground">{s.school_name} {s.school_needs_review && <span className="ml-1 text-[10px] text-amber-600">(review)</span>}</td>
                  <td className="px-3 py-2 text-foreground/70">{s.contact_email}</td>
                  <td className="px-3 py-2 text-foreground/70">{s.diagnostics_completed}/{s.diagnostics_total}</td>
                  <td className="px-3 py-2">{s.converted ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Paid</span> : <span className="text-foreground/60">{s.pilot_status}</span>}</td>
                  <td className="px-3 py-2 text-right">
                    {!s.converted && <Button size="sm" variant="outline" disabled={converting === s.tenant_id} onClick={() => convert("school", s.tenant_id, s.school_name)} data-testid={`pilot-convert-school-${s.tenant_id}`}>{converting === s.tenant_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Convert to paid"}</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 mb-2">Pilot portfolios</p>
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid="pilot-portfolios-table">
            <thead className="bg-muted/50 text-foreground/60 text-xs">
              <tr><th className="text-left px-3 py-2">Organization</th><th className="text-left px-3 py-2">Contact</th><th className="text-left px-3 py-2">Status</th><th className="px-3 py-2"></th></tr>
            </thead>
            <tbody>
              {(data.portfolios || []).length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-foreground/40">No pilot portfolios yet.</td></tr>}
              {(data.portfolios || []).map((p) => (
                <tr key={p.user_id} className="border-t border-border" data-testid={`pilot-portfolio-row-${p.user_id}`}>
                  <td className="px-3 py-2 font-medium text-foreground">{p.organization_name}</td>
                  <td className="px-3 py-2 text-foreground/70">{p.email}</td>
                  <td className="px-3 py-2">{p.converted ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />Paid</span> : <span className="text-foreground/60">{p.pilot_status}</span>}</td>
                  <td className="px-3 py-2 text-right">
                    {!p.converted && <Button size="sm" variant="outline" disabled={converting === p.user_id} onClick={() => convert("portfolio", p.user_id, p.organization_name)} data-testid={`pilot-convert-portfolio-${p.user_id}`}>{converting === p.user_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Convert to paid"}</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, hint, children }) => (
  <div className="flex flex-col gap-1.5">
    <Label className="text-xs font-medium text-foreground/70">{label}</Label>
    {children}
    {hint && <p className="text-[11px] text-foreground/40">{hint}</p>}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-background px-3 py-2.5">
    <span className="text-xs font-medium text-foreground/80">{label}</span>
    <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

export default function PilotSettings({ session }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState("config");

  useEffect(() => {
    pilotApi
      .getAdmin(session)
      .then(setCfg)
      .catch(() => toast.error("Failed to load pilot settings"))
      .finally(() => setLoading(false));
  }, [session]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const url = (cfg.button_url || "").trim();
    if (url && !url.toLowerCase().startsWith("https://")) {
      toast.error("Button URL must start with https://");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...cfg,
        supporting_points: Array.isArray(cfg.supporting_points)
          ? cfg.supporting_points
          : String(cfg.supporting_points || "").split("\n").map((s) => s.trim()).filter(Boolean),
      };
      const saved = await pilotApi.update(payload, session);
      setCfg(saved);
      toast.success("Pilot settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-foreground/50 py-10" data-testid="pilot-settings-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading pilot settings…
      </div>
    );
  }
  if (!cfg) return null;

  const points = Array.isArray(cfg.supporting_points) ? cfg.supporting_points.join("\n") : cfg.supporting_points || "";

  return (
    <div className="space-y-6" data-testid="pilot-settings">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Rocket className="h-4 w-4 text-primary" />
          <div>
            <h2 className="text-sm font-semibold text-foreground">Pilot Program</h2>
            <p className="text-xs text-foreground/40">Controls the public "Join the Pilot Program" section and pilot onboarding.</p>
          </div>
        </div>
        {view === "config" && (
          <Button onClick={save} disabled={saving} size="sm" data-testid="pilot-settings-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Save changes
          </Button>
        )}
      </div>

      <div className="inline-flex rounded-lg border border-border bg-muted p-1 gap-1">
        <button onClick={() => setView("config")} className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "config" ? "bg-background shadow-sm text-foreground" : "text-foreground/50"}`} data-testid="pilot-view-config">
          <Settings2 className="h-3.5 w-3.5" /> Configuration
        </button>
        <button onClick={() => setView("participants")} className={`px-3 py-1.5 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${view === "participants" ? "bg-background shadow-sm text-foreground" : "text-foreground/50"}`} data-testid="pilot-view-participants">
          <Users className="h-3.5 w-3.5" /> Participants
        </button>
      </div>

      {view === "participants" && <PilotParticipants session={session} />}
      {view === "config" && (
      <div className="space-y-6">
      {/* Program switches */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        <Toggle label="Program enabled" checked={cfg.program_enabled} onChange={(v) => set("program_enabled", v)} testid="pilot-toggle-program" />
        <Toggle label="Registration enabled" checked={cfg.registration_enabled} onChange={(v) => set("registration_enabled", v)} testid="pilot-toggle-registration" />
        <Toggle label="Landing section visible" checked={cfg.section_enabled} onChange={(v) => set("section_enabled", v)} testid="pilot-toggle-section" />
        <Toggle label="Single-school pilot" checked={cfg.single_school_enabled} onChange={(v) => set("single_school_enabled", v)} testid="pilot-toggle-single" />
        <Toggle label="Portfolio pilot" checked={cfg.portfolio_enabled} onChange={(v) => set("portfolio_enabled", v)} testid="pilot-toggle-portfolio" />
        <Toggle label="Send welcome emails" checked={cfg.welcome_email_enabled} onChange={(v) => set("welcome_email_enabled", v)} testid="pilot-toggle-welcome" />
      </div>

      {/* Landing content */}
      <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-4">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50">Landing section</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Row label="Eyebrow">
            <Input value={cfg.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} data-testid="pilot-field-eyebrow" />
          </Row>
          <Row label="Registration status">
            <Select value={cfg.registration_status || "open"} onValueChange={(v) => set("registration_status", v)}>
              <SelectTrigger data-testid="pilot-field-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="coming_soon">Coming soon</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </Row>
        </div>
        <Row label="Headline">
          <Input value={cfg.headline || ""} onChange={(e) => set("headline", e.target.value)} data-testid="pilot-field-headline" />
        </Row>
        <Row label="Description">
          <Textarea rows={3} value={cfg.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="pilot-field-description" />
        </Row>
        <Row label="Supporting points" hint="One per line.">
          <Textarea rows={5} value={points} onChange={(e) => set("supporting_points", e.target.value.split("\n"))} data-testid="pilot-field-points" />
        </Row>
        <Row label="Closed / coming-soon message" hint="Shown instead of the button when status is not Open.">
          <Textarea rows={2} value={cfg.closed_message || ""} onChange={(e) => set("closed_message", e.target.value)} data-testid="pilot-field-closed" />
        </Row>
      </div>

      {/* Button / link */}
      <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-4">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50 flex items-center gap-1.5">
          <ExternalLink className="h-3.5 w-3.5" /> Button &amp; registration link
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Row label="Button label">
            <Input value={cfg.button_label || ""} onChange={(e) => set("button_label", e.target.value)} data-testid="pilot-field-button-label" />
          </Row>
          <Row label="Button URL (Zoom / registration)" hint="Must be https://. Leave blank to send users to the built-in pilot Get Started flow.">
            <Input value={cfg.button_url || ""} onChange={(e) => set("button_url", e.target.value)} placeholder="https://zoom.us/webinar/register/..." data-testid="pilot-field-button-url" />
          </Row>
        </div>
        <Toggle label="Open link in a new tab" checked={cfg.open_in_new_tab} onChange={(v) => set("open_in_new_tab", v)} testid="pilot-toggle-newtab" />
      </div>

      {/* Onboarding defaults (Wave 2) */}
      <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-4">
        <p className="text-[11px] uppercase tracking-wider font-semibold text-foreground/50">Onboarding defaults</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Row label="License duration (days)">
            <Input type="number" value={cfg.default_license_duration_days ?? 90} onChange={(e) => set("default_license_duration_days", parseInt(e.target.value || "0", 10))} data-testid="pilot-field-license-days" />
          </Row>
          <Row label="Diagnostic limit">
            <Input type="number" value={cfg.default_diagnostic_limit ?? 1} onChange={(e) => set("default_diagnostic_limit", parseInt(e.target.value || "0", 10))} data-testid="pilot-field-diag-limit" />
          </Row>
          <Row label="School limit (portfolio)">
            <Input type="number" value={cfg.default_school_limit ?? 25} onChange={(e) => set("default_school_limit", parseInt(e.target.value || "0", 10))} data-testid="pilot-field-school-limit" />
          </Row>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Row label="Support email">
            <Input value={cfg.support_email || ""} onChange={(e) => set("support_email", e.target.value)} data-testid="pilot-field-support-email" />
          </Row>
          <Toggle label="Require sales contact" checked={cfg.require_sales_contact} onChange={(v) => set("require_sales_contact", v)} testid="pilot-toggle-sales" />
        </div>
      </div>

      {cfg.updated_at && (
        <p className="text-[11px] text-foreground/30">Last updated {new Date(cfg.updated_at).toLocaleString()}</p>
      )}
      </div>
      )}
    </div>
  );
}
